// ---------------------------------------------------------------------------
// voiceFidelity — pure, defensive reader for `voice_render.accent_fidelity`
// (VAF-12). The dashboard indexer projects a bounded, privacy-safe copy of the
// pipeline's acoustic receipts onto the SESSION profile
// (services/dashboard-indexer/voice_fidelity_projection.py in
// echoflicks-infra). This module re-validates that shape: unknown values are
// dropped, nothing ever throws.
//
// The CRM is an audit surface. It must never manufacture acceptance:
//   * the FINAL verdict comes only from `encoded_receipt` (stage "encoded");
//     a preview, saved-voice or take receipt is never promoted to it, and an
//     absent encoded receipt is "unverified" whatever earlier stages said;
//   * an evaluator verdict is an automated acoustic classification, not human
//     listening and not delivery.
// ---------------------------------------------------------------------------

import {
  normalizeLanguageCode,
  VENDORED_LANGUAGES,
} from "../contact-language/languages";

export const ACCENT_FAMILIES = [
  "american",
  "asian",
  "australian",
  "british",
  "french",
  "indian",
  "nigerian",
  "scottish",
  "spanish",
] as const;
export type AccentFamily = (typeof ACCENT_FAMILIES)[number];

export const FIDELITY_STAGES = [
  "preview",
  "saved_voice",
  "take",
  "encoded",
] as const;
export type FidelityStage = (typeof FIDELITY_STAGES)[number];

export const FIDELITY_STATUSES = [
  "accepted",
  "rejected",
  "uncertain",
  "unsupported",
  "unavailable",
] as const;
export type FidelityStatus = (typeof FIDELITY_STATUSES)[number];

export const REPORTED_STATUSES = [
  ...FIDELITY_STATUSES,
  "unverified",
  "not_applicable",
  "not_evaluated",
] as const;
export type ReportedStatus = (typeof REPORTED_STATUSES)[number];

export const POLICY_MODES = ["off", "observe", "enforce"] as const;
export type PolicyMode = (typeof POLICY_MODES)[number];

const STRENGTHS = ["natural", "subtle", "strong"] as const;
type AccentStrength = (typeof STRENGTHS)[number];
const SOURCES = ["explicit", "inferred", "curated"] as const;
type RequirementSource = (typeof SOURCES)[number];
// Regional details the trusted v4 compiler accepts (indexer DETAILS).
const DETAILS = [
  "southern british",
  "southern english",
  "contemporary london",
  "london",
  "glaswegian",
] as const;

const SHA256 = /^[a-f0-9]{64}$/;
const TOKEN = /^[A-Za-z0-9_.:/-]{1,160}$/;
const SLUG = /^[a-z0-9][a-z0-9._-]{2,63}$/;
const LANGUAGE = /^[a-z]{2}(?:-[A-Za-z]{2,8})?$/;
const MAX_RECEIPTS = 50;

export type AccentRequirement = {
  accent: AccentFamily;
  language: string;
  strength: AccentStrength;
  source: RequirementSource;
  detail: string | null;
};

export type FidelityReceipt = {
  stage: FidelityStage;
  status: FidelityStatus;
  audioSha256: string;
  requirementDigest: string;
  voiceId: string;
  modelId: string;
  evaluatorVersion: string;
  /** 0..1, the evaluator's own score; null when not reported. */
  confidence: number | null;
  /** Evaluator class slug (e.g. "scotland"), not a customer-facing label. */
  observedAccent: string | null;
  speechMs: number | null;
  windows: number | null;
  minWindowPermille: number | null;
};

export type FinalVerdict =
  | { kind: "evaluated"; status: FidelityStatus; receipt: FidelityReceipt }
  | { kind: "unverified" };

export type VoiceFidelityEvidence = {
  requirement: AccentRequirement | null;
  policyMode: PolicyMode | null;
  policyDigest: string | null;
  /** What the pipeline said overall; informational, never the verdict. */
  reportedStatus: ReportedStatus | null;
  receipts: FidelityReceipt[];
  encodedReceipt: FidelityReceipt | null;
  plannedVoiceId: string | null;
  effectiveVoiceId: string | null;
  plannedVoiceType: string | null;
};

/**
 * Parse `voice_render` (the SESSION profile attribute). Returns null when the
 * session carries no accent-fidelity evidence at all.
 */
export function parseVoiceFidelity(
  voiceRender: unknown,
): VoiceFidelityEvidence | null {
  const render = recordValue(voiceRender);
  const fidelity = recordValue(render?.accent_fidelity);
  if (!render || !fidelity) return null;

  const receipts = Array.isArray(fidelity.receipts)
    ? fidelity.receipts
        .slice(0, MAX_RECEIPTS)
        .map(parseFidelityReceipt)
        .filter((receipt): receipt is FidelityReceipt => receipt !== null)
    : [];
  const encoded = parseFidelityReceipt(fidelity.encoded_receipt);
  const evidence: VoiceFidelityEvidence = {
    requirement: parseRequirement(fidelity.requirement),
    policyMode: oneOf(fidelity.policy_mode, POLICY_MODES),
    policyDigest: digestValue(fidelity.policy_digest),
    reportedStatus: oneOf(fidelity.reported_status, REPORTED_STATUSES),
    receipts,
    encodedReceipt: encoded?.stage === "encoded" ? encoded : null,
    plannedVoiceId: tokenValue(render.planned_voice_id),
    effectiveVoiceId: tokenValue(render.effective_voice_id),
    plannedVoiceType: tokenValue(render.planned_voice_type),
  };
  const hasEvidence =
    evidence.requirement !== null ||
    evidence.policyMode !== null ||
    evidence.reportedStatus !== null ||
    evidence.receipts.length > 0 ||
    evidence.encodedReceipt !== null;
  return hasEvidence ? evidence : null;
}

export function parseFidelityReceipt(value: unknown): FidelityReceipt | null {
  const raw = recordValue(value);
  if (!raw) return null;
  const stage = oneOf(raw.stage, FIDELITY_STAGES);
  const status = oneOf(raw.status, FIDELITY_STATUSES);
  const audioSha256 = digestValue(raw.audio_sha256);
  const requirementDigest = digestValue(raw.requirement_digest);
  const voiceId = tokenValue(raw.voice_id);
  const modelId = tokenValue(raw.model_id);
  const evaluatorVersion = tokenValue(raw.evaluator_version);
  if (
    !stage ||
    !status ||
    !audioSha256 ||
    !requirementDigest ||
    !voiceId ||
    !modelId ||
    !evaluatorVersion
  ) {
    return null;
  }
  const confidence =
    typeof raw.confidence === "number" &&
    Number.isFinite(raw.confidence) &&
    raw.confidence >= 0 &&
    raw.confidence <= 1
      ? raw.confidence
      : null;
  const observed =
    typeof raw.observed_accent === "string" && SLUG.test(raw.observed_accent)
      ? raw.observed_accent
      : null;
  return {
    stage,
    status,
    audioSha256,
    requirementDigest,
    voiceId,
    modelId,
    evaluatorVersion,
    confidence,
    observedAccent: observed,
    speechMs: boundedInt(raw.speech_ms, 86_400_000),
    windows: boundedInt(raw.windows, 64),
    minWindowPermille: boundedInt(raw.min_window_permille, 1000),
  };
}

/** The final audio verdict: the encoded receipt, or unverified. */
export function finalVerdictOf(evidence: VoiceFidelityEvidence): FinalVerdict {
  const receipt = evidence.encodedReceipt;
  return receipt && receipt.stage === "encoded"
    ? { kind: "evaluated", status: receipt.status, receipt }
    : { kind: "unverified" };
}

/**
 * Receipts for the per-stage table: the reported list, plus the encoded
 * receipt when the list does not already carry an identical row. Pipeline
 * stage order; within a stage the reported order is kept.
 */
export function evidenceRows(
  evidence: VoiceFidelityEvidence,
): FidelityReceipt[] {
  const rows = [...evidence.receipts];
  const encoded = evidence.encodedReceipt;
  if (encoded && !rows.some((row) => sameReceipt(row, encoded))) {
    rows.push(encoded);
  }
  return rows
    .map((row, position) => ({ row, position }))
    .sort(
      (left, right) =>
        FIDELITY_STAGES.indexOf(left.row.stage) -
          FIDELITY_STAGES.indexOf(right.row.stage) ||
        left.position - right.position,
    )
    .map(({ row }) => row);
}

/** Honesty checks an operator should see next to the verdict. */
export function evidenceWarnings(evidence: VoiceFidelityEvidence): string[] {
  const warnings: string[] = [];
  const verdict = finalVerdictOf(evidence);
  if (verdict.kind === "unverified" && evidence.reportedStatus === "accepted") {
    warnings.push(
      "The pipeline reported accepted, but no final encoded-audio receipt backs it.",
    );
  }
  if (
    verdict.kind === "evaluated" &&
    evidence.reportedStatus &&
    (FIDELITY_STATUSES as readonly string[]).includes(
      evidence.reportedStatus,
    ) &&
    evidence.reportedStatus !== verdict.status
  ) {
    warnings.push(
      `The pipeline-reported status (${evidence.reportedStatus}) differs from the final encoded receipt (${verdict.status}).`,
    );
  }
  if (
    verdict.kind === "evaluated" &&
    evidence.effectiveVoiceId &&
    verdict.receipt.voiceId !== evidence.effectiveVoiceId
  ) {
    warnings.push(
      "The final receipt was recorded for a different voice than the effective narration voice.",
    );
  }
  if (
    evidence.plannedVoiceId &&
    evidence.effectiveVoiceId &&
    evidence.plannedVoiceId !== evidence.effectiveVoiceId
  ) {
    warnings.push(
      "The effective narration voice differs from the intended (planned) voice.",
    );
  }
  const digests = new Set(
    evidenceRows(evidence).map((row) => row.requirementDigest),
  );
  if (digests.size > 1) {
    warnings.push(
      `Receipts reference ${digests.size} different accent requirements (requirement digests disagree).`,
    );
  }
  if (!evidence.requirement && evidenceRows(evidence).length > 0) {
    warnings.push(
      "Receipts exist, but the accent requirement was not projected, so they cannot be read against a request.",
    );
  }
  return warnings;
}

// ---------------------------------------------------------------------------
// Wording. Shared by the Echo panel and the Sound page aggregate.
// ---------------------------------------------------------------------------

const ACCENT_LABELS: Record<AccentFamily, string> = {
  american: "American",
  asian: "Asian",
  australian: "Australian",
  british: "British",
  french: "French",
  indian: "Indian",
  nigerian: "Nigerian",
  scottish: "Scottish",
  spanish: "Spanish",
};

const SOURCE_LABELS: Record<RequirementSource, string> = {
  explicit: "explicitly requested",
  inferred: "inferred by the pipeline (not explicitly requested)",
  curated: "from a curated voice reference",
};

const STAGE_LABELS: Record<FidelityStage, string> = {
  preview: "Voice preview",
  saved_voice: "Saved voice",
  take: "Narration take",
  encoded: "Final encoded audio",
};

/**
 * English name of the requirement's language from the echo-language registry
 * ("ru" -> "Russian", "en-GB" -> "English"); a code outside it stays as the
 * upper-cased tag.
 */
function languageName(tag: string): string {
  const code = normalizeLanguageCode(tag);
  return (
    VENDORED_LANGUAGES.find((language) => language.code === code)
      ?.english_label ?? tag.toUpperCase()
  );
}

export function accentLabel(accent: AccentFamily): string {
  return ACCENT_LABELS[accent];
}

export function stageLabel(stage: FidelityStage): string {
  return STAGE_LABELS[stage];
}

/** "Scottish English (Glaswegian), natural strength, explicitly requested". */
export function requirementLabel(requirement: AccentRequirement): string {
  const language = languageName(requirement.language);
  const detail = requirement.detail
    ? ` (${requirement.detail[0].toUpperCase()}${requirement.detail.slice(1)})`
    : "";
  return `${accentLabel(requirement.accent)} ${language}${detail}, ${
    requirement.strength
  } strength, ${SOURCE_LABELS[requirement.source]}`;
}

export function policyModeLabel(mode: PolicyMode | null): string {
  switch (mode) {
    case "off":
      return "Off — no accent evaluation was configured";
    case "observe":
      return "Observe — evaluator results are recorded only and do not gate the narration";
    case "enforce":
      return "Enforce — the pipeline is configured to act on evaluator verdicts";
    default:
      return "Not reported";
  }
}

/** Status wording for one receipt (any stage). */
export function receiptStatusLabel(status: FidelityStatus): string {
  switch (status) {
    case "accepted":
      return "Automated evaluator: accepted (not human listening)";
    case "rejected":
      return "Automated evaluator: rejected";
    case "uncertain":
      return "Automated evaluator: uncertain";
    case "unsupported":
      return "Unsupported by evaluator";
    case "unavailable":
      return "Evaluator unavailable";
  }
}

export function finalVerdictLabel(verdict: FinalVerdict): string {
  return verdict.kind === "unverified"
    ? "Unverified — no final audio verdict"
    : receiptStatusLabel(verdict.status);
}

export type VerdictTone = "positive" | "negative" | "caution";

export function statusTone(status: FidelityStatus | "unverified"): VerdictTone {
  if (status === "accepted") return "positive";
  if (status === "rejected") return "negative";
  return "caution";
}

export function reportedStatusLabel(status: ReportedStatus): string {
  switch (status) {
    case "unverified":
      return "unverified";
    case "not_applicable":
      return "not applicable";
    case "not_evaluated":
      return "not evaluated";
    default:
      return status;
  }
}

export function formatPercent(value: number | null): string {
  return value === null ? "—" : `${Math.round(value * 100)}%`;
}

export function formatPermille(value: number | null): string {
  return value === null
    ? "—"
    : `${(value / 10).toFixed(1).replace(/\.0$/, "")}%`;
}

export function formatSpeechSeconds(speechMs: number | null): string {
  return speechMs === null ? "—" : `${(speechMs / 1000).toFixed(1)} s`;
}

export function shortHash(value: string): string {
  return value.slice(0, 12);
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function parseRequirement(value: unknown): AccentRequirement | null {
  const raw = recordValue(value);
  if (!raw) return null;
  const accent = oneOf(raw.accent, ACCENT_FAMILIES);
  const strength = oneOf(raw.strength, STRENGTHS);
  const source = oneOf(raw.source, SOURCES);
  const language =
    typeof raw.language === "string" && LANGUAGE.test(raw.language)
      ? raw.language
      : null;
  if (!accent || !strength || !source || !language) return null;
  const detail =
    typeof raw.detail === "string"
      ? oneOf(raw.detail.trim().toLowerCase(), DETAILS)
      : null;
  return { accent, language, strength, source, detail };
}

function sameReceipt(left: FidelityReceipt, right: FidelityReceipt): boolean {
  return (
    left.stage === right.stage &&
    left.status === right.status &&
    left.audioSha256 === right.audioSha256 &&
    left.requirementDigest === right.requirementDigest &&
    left.voiceId === right.voiceId &&
    left.modelId === right.modelId &&
    left.evaluatorVersion === right.evaluatorVersion
  );
}

function oneOf<T extends string>(
  value: unknown,
  allowed: readonly T[],
): T | null {
  return typeof value === "string" &&
    (allowed as readonly string[]).includes(value)
    ? (value as T)
    : null;
}

function digestValue(value: unknown): string | null {
  return typeof value === "string" && SHA256.test(value) ? value : null;
}

function tokenValue(value: unknown): string | null {
  return typeof value === "string" && TOKEN.test(value) ? value : null;
}

function boundedInt(value: unknown, ceiling: number): number | null {
  return typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 0 &&
    value <= ceiling
    ? value
    : null;
}

function recordValue(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}
